/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Collapse, Form, Input, Row, Select, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import CustomInputNumber from '../../../Forms/Fields/CustomInputNumber';
import { ArrowDownIcon } from '../../../Icons/ArrowDownIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../../../constants/regex';
import { useParams, useSearchParams } from 'react-router-dom';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { ACTIVITY_TYPES, VARIABLE_TYPES } from '../../../../constants/main';
import { SENDBOX_STATUSES } from '../../../../constants/flowGraph';

// Constant declarations
const { Text } = Typography;

const FileUploadNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const { orgId, tenantId } = useParams();
  const [searchParams] = useSearchParams();

  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));

  return (
    <Fragment>
      <LabelWithOriginalText label={t('Prompt text')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />
      {
        botLanguages.map(lang => (
          <Form.Item
            label=""
            name={['data', 'label', lang]}
            hidden={currentBotLanguage !== lang}
            rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Prompt text cannot be blank.') }]}
          >
            <RichTextEditor
              placeholder={t('Enter a message the user will see before uploading the file')}
              includedVariableTypes={activity?.type === ACTIVITY_TYPES.LIVECHAT_FLOW ? [VARIABLE_TYPES.LIVECHAT] : []}
            />
          </Form.Item>
        ))
      }
      <Form.Item
        label={t('Set supported file formats')}
        name={['data', 'supportedFileTypes']}
      >
        <Select
          mode="tags"
          placeholder={t('Enter supported file types')}
          size="large"
          getPopupContainer={triggerNode => triggerNode.parentElement}
          disabled={!isEditingPrimaryLanguage}
          options={[
            { label: 'JPG', value: 'jpg' },
            { label: 'PNG', value: 'png' },
            { label: 'PDF', value: 'pdf' },
            { label: 'DOC', value: 'doc' },
            { label: 'DOCX', value: 'docx' },
            { label: 'CSV', value: 'csv' },
            { label: 'XLS', value: 'xls' },
            { label: 'XLSX', value: 'xlsx' },
            { label: 'PPT', value: 'ppt' },
            { label: 'PPTX', value: 'pptx' },
            { label: 'TXT', value: 'txt' },
            { label: 'ZIP', value: 'zip' },
            { label: 'RAR', value: 'rar' }
          ]}
        />
      </Form.Item>

      <Row gutter={[16, 8]}>
        <Col span={24}>
          <Text>
            {t('Set maximum upload file size')}
            <Tooltip
              className="ml-1"
              overlayStyle={{ maxWidth: 350 }}
              title={t('Cannot be larger than 5MB')}
            >
              <QuestionCircleOutlined className="v-align-top fs-base" />
            </Tooltip>
          </Text>
        </Col>

        <Col>
          <Form.Item
            name={['data', 'maxFileSize']}
            // add a 5MB limit custom validator
            rules={[
              {
                validator: (rule, value) => {
                  if (value > 5 && form.getFieldValue(['data', 'maxFileSizeUnit']) === 'MB') {
                    return Promise.reject(t('Cannot be larger than 5MB'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <CustomInputNumber
              min={1}
              max={form.getFieldValue(['data', 'maxFileSizeUnit']) === 'MB' ? 5 : 5000}
              size="large"
              placeholder={'-'}
            />
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name={['data', 'maxFileSizeUnit']}
            initialValue={'MB'}
          >
            <Select
              size="large"
              getPopupContainer={triggerNode => triggerNode.parentElement}
              options={[
                { label: 'MB', value: 'MB' },
                { label: 'KB', value: 'KB' }
              ]}
              // On change also validate the maxFileSize
              onChange={(value) => {
                if (form.getFieldValue(['data', 'maxFileSize']) > 5 && value === 'MB') {
                  form.setFieldsValue({ 'data': { ...form.getFieldValue(['data']), 'maxFileSize': 5 } });
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <ArrowDownIcon rotate={isActive ? 180 : 0} />}
      >
        <Collapse.Panel
          header={t('Advanced Settings')}
          forceRender
        >
          <Form.Item
            name={['data', 'questionId']}
            label={<Text>
              {t('Variable name')}
              <Tooltip
                className="ml-1"
                placement="top"
                title={t('It is used for storing the answer to this question. Set a variable name to use the answer in messages templates, or in control flow nodes.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Text>}
          >
            <Input
              placeholder={t('Enter the variable name')}
              size="large"
              onKeyPress={(e) => {
                if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                  e.preventDefault();
                  return false;
                }
              }}
              maxLength={30}
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>

          <Form.Item
            label={t('Sendbox status')}
            name={['data', 'sendboxState']}
            className="mt-3"
          >
            <Select
              placeholder={t('Choose the status of the sendbox')}
              size="large"
              getPopupContainer={triggerNode => triggerNode.parentElement}
              defaultValue={SENDBOX_STATUSES.DEFAULT}
              options={
                Object.entries(SENDBOX_STATUSES).map(([key, value]) => ({
                  label: t(key, { context: 'sendbox status' }),
                  value: value
                }))
              }
            />
          </Form.Item>

          <LabelWithOriginalText label={t('Message on uploading failure')} originalText={form.getFieldValue(['data', 'fileUploadFallbackMessage', primaryBotLanguage])} />
          {
            botLanguages.map(lang => (
              <Form.Item
                label=""
                name={['data', 'fileUploadFallbackMessage', lang]}
                hidden={currentBotLanguage !== lang}
              >
                <RichTextEditor
                  placeholder={t('Write an error message that the user will see if the upload fails')}
                />
              </Form.Item>
            ))
          }
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
};

FileUploadNodeForm.propTypes = {
  form: PropTypes.object.isRequired
};

export default FileUploadNodeForm;
